import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    JournalEntry,
    LocaleCode,
    PayrollRun,
    PayrollRunSummary,
    ValidationState,
} from '@/api/models';
import {
    useCommitPayrollRunMutation,
    useInvalidatePostedPayrollEntry,
    usePreviewPayrollRunMutation,
} from '@/api/queries';
import { todayIso } from '@/pages/accounting/statements/statementMeta';
import {
    periodFromDate,
    salaryErrorMessage,
    toRunPreviewRows,
    toRunTotalsRow,
    type RateText,
    type RunPreviewRow,
    type RunTotalsRow,
} from '../../salariesMeta';
import { useSalaryRatesForPeriod } from '../../useSalaryRates';

type UsePayrollRunParams = {
    locale: LocaleCode;
    onClose: () => void;
    /** Open the run detail for a period that has already been run. */
    onOpenExistingRun: (journalEntryId: string) => void;
};

export type PayrollRunModel = {
    /** The date whose month is being run (the picker speaks dates). */
    periodDate: string;
    period: string;
    onPeriodDateChange: (value: string) => void;
    /** The rates in force for this period; null when none are. */
    rates: RateText | null;
    isPreviewing: boolean;
    isCommitting: boolean;
    error: string | null;
    /** The computed run; null before the first preview for this period. */
    rows: RunPreviewRow[];
    totals: RunTotalsRow | null;
    hasPreview: boolean;
    /** True when the preview produced no gross at all. */
    isNilRun: boolean;
    /**
     * The draft the preview says a commit would build — rendered, never opened.
     * Its `validationState` already accounts for the accounting-period gate.
     */
    previewValidation: ValidationState | null;
    /** Non-null when this period already has a run. Warn before committing. */
    existingRun: PayrollRunSummary | null;
    onPreview: () => Promise<void>;
    onOpenExisting: () => void;
    /** Create the period's draft entry and open it in the Journal Entry form. */
    onReview: () => Promise<void>;
    draft: JournalEntry | null;
    onDraftClose: () => void;
    onDraftSaved: (entry: JournalEntry) => void;
};

/**
 * The payroll run.
 *
 * Two steps, and the second is where this differs from the mock era:
 *
 * 1. **Preview** asks what the period would pay. It writes nothing. Every
 *    employee comes back with a line, charged or explained, which is the whole
 *    reason it returns the register rather than only the paid.
 * 2. **Review** *commits*: the server creates one balanced DRAFT entry and
 *    returns it, and that entry opens in the Journal Entry form. There is no
 *    third step — the server built the entry, so there is nothing for the client
 *    to post back and verify. The old `ENTRY_MISMATCH` round-trip is gone with
 *    it.
 *
 * **Committing posts nothing**, and that is the fact the whole screen turns on.
 * The draft sits in the journal until a person posts it there, under the
 * journal's own validation and period gating. Until it posts, nothing payroll
 * has accrued moves — which is why posting, not committing, is what invalidates
 * the register.
 *
 * A second run for a period is impossible rather than merely discouraged: the
 * server is idempotent per period, so a repeat commit returns the run that
 * exists. `existingRun` surfaces that state up front, before the user asks.
 */
export const usePayrollRun = ({
    locale,
    onClose,
    onOpenExistingRun,
}: UsePayrollRunParams): PayrollRunModel => {
    const { t } = useTranslation('pages');
    const preview = usePreviewPayrollRunMutation();
    const commit = useCommitPayrollRunMutation();
    const invalidatePosted = useInvalidatePostedPayrollEntry();

    const [periodDate, setPeriodDate] = React.useState(todayIso);
    const [run, setRun] = React.useState<PayrollRun | null>(null);
    const [previewValidation, setPreviewValidation] =
        React.useState<ValidationState | null>(null);
    const [hasDraftToCommit, setHasDraftToCommit] = React.useState(false);
    const [existingRun, setExistingRun] =
        React.useState<PayrollRunSummary | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    /**
     * The persisted draft the commit created, handed to the form as it arrives.
     *
     * It briefly could not be: the journal API used to refuse an etag that came
     * from a *write* response on the next write, so pressing Post in the form
     * answered `409 STALE_UPDATE`, and this held only the entry's id and re-read
     * it through `GET /{id}` to get a usable token. **BOS-111 fixed the cause** —
     * the etag was hashed from a raw .NET tick count, which `timestamp` cannot
     * store, so the entity in memory and the row read back disagreed in their
     * last digit. Hashing at microsecond precision makes both sides agree, and
     * the extra round-trip is gone.
     */
    const [draft, setDraft] = React.useState<JournalEntry | null>(null);

    const period = periodFromDate(periodDate);
    // The rates the *server* will apply to this period — resolved on its end
    // date, so running June after a July rate change still quotes June's rates.
    const rates = useSalaryRatesForPeriod(period);

    // Changing the month invalidates whatever was on screen — a preview belongs
    // to exactly one period and must never be read against another.
    React.useEffect(() => {
        setRun(null);
        setPreviewValidation(null);
        setHasDraftToCommit(false);
        setExistingRun(null);
        setError(null);
    }, [period]);

    const onPreview = React.useCallback(async () => {
        setError(null);
        const result = await preview.mutateAsync({ period, locale });
        if (!result.success) {
            // `NO_RATES_IN_FORCE` lands here and gets its own message: no rate
            // set governs this period, and the remedy is tax settings, not a
            // retry.
            setError(salaryErrorMessage(result.error, t));
            return;
        }
        setRun(result.response.run);
        setPreviewValidation(result.response.draft?.validationState ?? null);
        setHasDraftToCommit(result.response.draft !== null);
        setExistingRun(result.response.existingRun);
    }, [preview, period, locale, t]);

    const onOpenExisting = React.useCallback(() => {
        if (existingRun) {
            onOpenExistingRun(existingRun.journalEntryId);
        }
    }, [existingRun, onOpenExistingRun]);

    /**
     * Create the run and open its draft.
     *
     * The preview's draft cannot be opened directly — it is a
     * `DraftEntryPreview` with no id, because nothing behind it exists yet. The
     * commit is what turns it into a real entry, and its response is the only
     * place a saved draft comes from.
     */
    const onReview = React.useCallback(async () => {
        setError(null);
        const result = await commit.mutateAsync({ period, locale });
        if (!result.success) {
            setError(salaryErrorMessage(result.error, t));
            return;
        }
        // `alreadyExisted` is not an error: the server handed back the run this
        // period already had, along with its entry, which is exactly the
        // no-duplicates guarantee.
        setExistingRun(null);
        setDraft(result.response.draft);
    }, [commit, period, locale, t]);

    const onDraftClose = React.useCallback(() => {
        // The draft stays — it is persisted, and closing the form is not a
        // withdrawal. Previewing this period again will now report it as an
        // existing run.
        setDraft(null);
    }, []);

    const onDraftSaved = React.useCallback(
        (entry: JournalEntry) => {
            // There is no commit to make — the run was recorded when the draft
            // was created. But the *register* has to be told, and only on a
            // post: posting is what moves every accrued figure and the ledger
            // side of the comparison, and the journal's own post mutation
            // invalidates only the journal. A save that left the entry a DRAFT
            // moves nothing, so it leaves the form open and changes nothing
            // here.
            invalidatePosted(entry);
            if (entry.status !== 'POSTED') {
                return;
            }
            setDraft(null);
            onClose();
        },
        [invalidatePosted, onClose],
    );

    const rows = React.useMemo(
        () => (run ? toRunPreviewRows(run.lines, locale) : []),
        [run, locale],
    );

    const totals = React.useMemo(
        () => (run ? toRunTotalsRow(run.totals) : null),
        [run],
    );

    return {
        periodDate,
        period,
        onPeriodDateChange: setPeriodDate,
        rates,
        isPreviewing: preview.isPending,
        // The read of the committed entry is part of the same user action, so
        // the button stays pending through it rather than flickering back and
        // inviting a second press.
        isCommitting: commit.isPending,
        error,
        rows,
        totals,
        hasPreview: run !== null,
        isNilRun: run !== null && !hasDraftToCommit,
        previewValidation,
        existingRun,
        onPreview,
        onOpenExisting,
        onReview,
        draft,
        onDraftClose,
        onDraftSaved,
    };
};
